import React, { useState, useContext, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  FormControl,
  MenuItem,
  Select,
  Backdrop,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { ThemeContext } from "../../context/ThemeContext";
import { getColors } from "../../themes/theme";
import AddUserDialog from "../../components/users/AddUserDialog";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import { EditUserDialog } from "../../components/users/EditUserDialog";

const UserPage = () => {
  const { user, token } = useAuthContext();

  const { darkMode } = useContext(ThemeContext);
  const [openDialog, setOpenDialog] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState([]); // State for filtered practices
  const [searchQuery, setSearchQuery] = useState(""); // State for search query
  const [userRole, setUserRole] = useState(""); // To track if user is admin or not
  const [users, setUsers] = useState([]); // State to store users fetched from backend
  const [page, setPage] = useState(0); // Pagination page
  const [rowsPerPage, setRowsPerPage] = useState(10); // Users per page
  const [loading, setLoading] = useState(true); // Loading state for fetching data
  const { enqueueSnackbar } = useSnackbar();

  const [openEditDialog, setOpenEditDialog] = useState(false); // New state for confirmation dialog
  const [userToEdit, setUserToEdit] = useState(null); // New state for the user to be deleted

  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: "username", // Default sort key
    direction: "asc", // Default sort direction
  });

  const userStatusResult = (subscribed, status) => {
    if (subscribed === 1 && status === 0) return "Active";
    if (subscribed === 1 && status === 1) return "Suspended";
    if (subscribed === 1 && status === 2) return "N/A";
    if (subscribed === 0 && status === 0) return "Trial";
    if (subscribed === 0 && status === 1) return "Suspended";
    if (subscribed === 0 && status === 2) return "Pending";
    return "Unknown"; // Default case for unexpected values
  };

  useEffect(() => {
    // Fetch users from the backend
    if (!user || !token) return;
    const fetchUsers = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/users`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const data = response.data;

        // Map the backend response to match your data structure
        const mappedUsers = data?.users.map((user) => ({
          userid: user.userid,
          username: user.username,
          email: user.email || "N/A",
          description: user.description || "N/A",
          subscribed: user.subscribed,
          status: user.suspended,
          role: user.securitytype,
        }));

        setUsers(mappedUsers);
        setFilteredUsers(mappedUsers);
      } catch (err) {
        console.error("Error fetching users:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [token, user]);

  useEffect(() => {
    // Filter practices based on the search query
    setFilteredUsers(
      users.filter((user) => {
        const statusText = userStatusResult(user.subscribed, user.status);

        const roleText =
          user.role === 1
            ? "Global Admin"
            : user.role === 2
            ? "User Admin"
            : user.role === 3
            ? "User"
            : "";

        return (
          (user.username || "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          (user.email || "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          (user.description || "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          statusText.toLowerCase().includes(searchQuery.toLowerCase()) ||
          roleText.toLowerCase().includes(searchQuery.toLowerCase())
        );
      })
    );
  }, [searchQuery, users]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleRoleChange = async (event, userId) => {
    const newRole = event.target.value;

    try {
      // Send a PUT request to the backend API to update the user role
      await axios.put(
        `${process.env.REACT_APP_API_URL}/users/${userId}`,
        {
          securitytype: newRole, // Assuming 'securitytype' represents the role
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      enqueueSnackbar("Successfully Changed!", { variant: "success" });
      // If successful, update the role of the user locally
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.userid === userId ? { ...user, role: newRole } : user
        )
      );
    } catch (err) {
      console.error("Error updating user role:", err);
    }
  };

  const handleEdit = async (userId) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/users/${userId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    setUserToEdit(response.data.user);
    setOpenEditDialog(true);
  };

  const handleCancelEdit = () => {
    setOpenEditDialog(false);
  };

  const handleDeleteUser = async (id) => {
    await axios.delete(`${process.env.REACT_APP_API_URL}/users/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    setUsers((prevUsers) => prevUsers.filter((user) => user.userid !== id));
    setOpenEditDialog(false);
    enqueueSnackbar("Successfully Deleted!", { variant: "success" });
  };

  const handleEditUser = async (data) => {
    await axios.put(
      `${process.env.REACT_APP_API_URL}/users/${userToEdit.userid}`,
      {
        username: data.username,
        password: data.password,
        email: data.email,
        description: data.description,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.userid === userToEdit.userid
          ? {
              ...user,
              username: data.username,
              password: data.password,
              email: data.email,
              description: data.description,
            }
          : user
      )
    );
    setOpenEditDialog(false);
    enqueueSnackbar("Successfully Changed!", { variant: "success" });
  };

  const handleAddAdminClicked = () => {
    setUserRole("Admin");
    setOpenDialog(true);
  };

  const handleAddUser = async (newUser) => {
    const newUserData = {
      ...newUser,
      suspended: 0,
      subscribed: 1,
      patientid: 0,
      practiceid: user.practiceId,
      securitytype: 1,
    };

    // Send a POST request to the backend API to add the new user
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/users`,
      newUserData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    setUsers((prevUsers) => [
      ...prevUsers,
      {
        ...response.data.user,
        status: response.data.user.suspended,
        role: response.data.user.securitytype,
      },
    ]);
    enqueueSnackbar("Successfully Added!", { variant: "success" });
    setOpenDialog(false);
  };

  const handleSort = (column) => {
    const isAscending =
      sortConfig.key === column && sortConfig.direction === "asc";
    const newDirection = isAscending ? "desc" : "asc";

    setSortConfig({
      key: column,
      direction: newDirection,
    });

    // Sort users based on the column and direction
    const sortedUsers = [...users].sort((a, b) => {
      if (a[column] < b[column]) return newDirection === "asc" ? -1 : 1;
      if (a[column] > b[column]) return newDirection === "asc" ? 1 : -1;
      return 0;
    });

    setUsers(sortedUsers);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const roleColors = {
    1: "#FF0000", // Admin
    2: "#00AF4E", // Global User
    3: "#0A7CD9", // User
  };

  return (
    <Box
      sx={{
        pt: "60px",
        pl: 5,
        pr: 10,
      }}
    >
      {/* Loading State */}
      <Backdrop sx={{ zIndex: 1400 }} open={loading}>
        <CircularProgress size={80} />
      </Backdrop>

      {!loading && (
        <>
          <Typography
            sx={{
              fontFamily: "Poppins",
              fontSize: "28px",
              color: darkMode
                ? getColors.drawerTextDark
                : getColors.thumbnailTextLight,
              fontWeight: 600,
              textAlign: "left",
              mb: 5,
            }}
          >
            Global User Administration
          </Typography>
          <Box display="flex" justifyContent="space-between" sx={{ mb: 3 }}>
            <TextField
              variant="filled"
              placeholder="Search users"
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 0 }}>
                    <SearchIcon sx={{ mt: -2, ml: 1 }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              startIcon={<Add />}
              variant="contained"
              sx={{
                fontFamily: "Poppins",
                fontSize: "18px",
                fontWeight: 500,
                px: 3,
                py: 2,
                background: darkMode ? "#2D2E30" : "#E6E6E6",
                color: darkMode
                  ? getColors.primaryTextDark
                  : getColors.secondaryTextLight,
                "&:hover": {
                  color: getColors.primaryTextDark,
                },
              }}
              onClick={handleAddAdminClicked}
            >
              Add Global Administrator
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ mb: 1 }}>
            <Table>
              <TableHead
                sx={{
                  background: darkMode ? "#1C1D21" : "#E6E6E6",
                  borderWidth: 1,
                  borderStyle: "solid",
                  borderColor: darkMode
                    ? getColors.appBarBorderDark
                    : getColors.thumbnailBorderLight,
                }}
              >
                <TableRow sx={{ height: "60px" }}>
                  {["username", "email", "description", "status", "role"].map(
                    (header, index) => (
                      <TableCell
                        key={index}
                        sx={{
                          borderColor: darkMode
                            ? getColors.appBarBorderDark
                            : getColors.thumbnailBorderLight,
                          px: 0,
                          cursor: "pointer",
                        }}
                        onClick={() => handleSort(header)}
                      >
                        <Box
                          display="flex"
                          sx={{
                            width: "100%",
                            height: "100%",
                            alignItems: "center",
                            fontFamily: "Poppins",
                            fontWeight: 500,
                            fontSize: "20px",
                            color: darkMode
                              ? getColors.listItemTextDark
                              : getColors.thumbnailTextLight,
                            ...(header === "username" ||
                            header === "email" ||
                            header === "description"
                              ? {
                                  px: "20px",
                                }
                              : { justifyContent: "center" }),
                          }}
                        >
                          {header.toUpperCase()}
                          {sortConfig.key === header && (
                            <>
                              {sortConfig.direction === "asc" ? (
                                <ArrowDownwardIcon sx={{ ml: 1 }} />
                              ) : (
                                <ArrowUpwardIcon sx={{ ml: 1 }} />
                              )}
                            </>
                          )}
                        </Box>
                      </TableCell>
                    )
                  )}
                  <TableCell
                    sx={{
                      fontFamily: "Poppins",
                      fontWeight: 500,
                      fontSize: "20px",
                      color: darkMode
                        ? getColors.listItemTextDark
                        : getColors.thumbnailTextLight,
                      textAlign: "center",
                      borderColor: darkMode
                        ? getColors.appBarBorderDark
                        : getColors.thumbnailBorderLight,
                    }}
                  >
                    {/* ACTIONS */}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedUsers.map((user) => (
                  <TableRow
                    key={user.userid}
                    sx={{
                      height: "60px",
                      borderWidth: 1,
                      borderStyle: "solid",
                      borderColor: darkMode
                        ? getColors.appBarBorderDark
                        : getColors.thumbnailBorderLight,
                    }}
                  >
                    <TableCell
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 500,
                        fontSize: "20px",
                        color: darkMode
                          ? getColors.listItemTextDark
                          : getColors.thumbnailTextLight,
                        px: "20px",
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: darkMode
                          ? getColors.appBarBorderDark
                          : getColors.thumbnailBorderLight,
                      }}
                    >
                      {user.username}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 400,
                        fontSize: "20px",
                        color: darkMode
                          ? getColors.listItemTextDark
                          : getColors.thumbnailTextLight,
                        px: "20px",
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: darkMode
                          ? getColors.appBarBorderDark
                          : getColors.thumbnailBorderLight,
                      }}
                    >
                      {user.email}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 400,
                        fontSize: "20px",
                        color: darkMode
                          ? getColors.listItemTextDark
                          : getColors.thumbnailTextLight,
                        px: "20px",
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: darkMode
                          ? getColors.appBarBorderDark
                          : getColors.thumbnailBorderLight,
                      }}
                    >
                      {user.description}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 400,
                        fontSize: "20px",
                        color: (() => {
                          const status = userStatusResult(
                            user.subscribed,
                            user.status
                          );
                          return status === "Active"
                            ? "#00AF4E"
                            : status === "Suspended"
                            ? "#FF0000"
                            : status === "Trial"
                            ? "#0000FF"
                            : status === "Pending"
                            ? "#FFA500"
                            : "#808080"; // Default color for unknown status
                        })(),
                        px: "20px",
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: darkMode
                          ? getColors.appBarBorderDark
                          : getColors.thumbnailBorderLight,
                        textAlign: "center",
                      }}
                    >
                      {userStatusResult(user.subscribed, user.status)}
                    </TableCell>

                    <TableCell
                      sx={{
                        textAlign: "center",
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: darkMode
                          ? getColors.appBarBorderDark
                          : getColors.thumbnailBorderLight,
                        padding: 0,
                      }}
                    >
                      <FormControl>
                        <Select
                          value={user.role}
                          onChange={(e) => handleRoleChange(e, user.userid)}
                          sx={{
                            width: "180px",
                            fontFamily: "Inter",
                            fontWeight: 500,
                            fontSize: "18px",
                            color: roleColors[user.role],
                          }}
                        >
                          <MenuItem value={1}>Global Admin</MenuItem>
                          <MenuItem value={2}>User Admin</MenuItem>
                          <MenuItem value={3}>User</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "center",
                        borderColor: darkMode
                          ? getColors.appBarBorderDark
                          : getColors.thumbnailBorderLight,
                      }}
                    >
                      <Box
                        display={"flex"}
                        justifyContent={"center"}
                        alignItems={"center"}
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleEdit(user.userid)}
                      >
                        <Box
                          display={"flex"}
                          justifyContent={"flex-end"}
                          width="max-content"
                          sx={{
                            borderRadius: "50%",
                            padding: "8px",
                            color: "#007FFF",
                            bgcolor: "#E9F3FF",
                            alignItems: "center",
                            "&:hover": {
                              color: "blue",
                            },
                          }}
                        >
                          <BorderColorOutlinedIcon />
                        </Box>
                        <Typography
                          sx={{
                            ml: "13px",
                            fontFamily: "Inter",
                            fontWeight: 500,
                            fontSize: "18px",
                            color: darkMode
                              ? getColors.listItemTextDark
                              : getColors.thumbnailTextLight,
                          }}
                        >
                          {/* Edit */}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            component="div"
            count={filteredUsers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />

          <EditUserDialog
            open={openEditDialog}
            onClose={handleCancelEdit}
            userData={userToEdit}
            onDelete={handleDeleteUser}
            onEdit={handleEditUser}
          ></EditUserDialog>

          {/* Dialog for adding User/Admin */}
          <AddUserDialog
            open={openDialog}
            setOpen={setOpenDialog}
            role={userRole}
            onAddUser={handleAddUser}
          />
        </>
      )}
    </Box>
  );
};

export default UserPage;
